/**
 * Path templates for every route the app navigates to, in-product or
 * otherwise. This is the runtime half of the URL registry: `buildPath`,
 * `useAppNavigate`, and `src/app-shell/routeMounts.ts` read it, so it ships
 * in the main bundle and holds nothing but the path strings.
 *
 * Each key must also have an entry in `registry.ts`, which holds the route's
 * label, description, and query params for the staffing-chat agent. That
 * catalog is large and only needed by the sync script, so it is kept out of
 * this module (a `satisfies` clause there fails the type check when the two
 * key sets drift).
 *
 * `as const` keeps each template's string literal type so
 * `PathParams<typeof paths.JOBS_EDITOR>` in buildPath.ts extracts the
 * `:param` names.
 */

export const paths = {
  // ---------------------------------------------------------------------------
  // LLM-navigable: top-level pages
  // ---------------------------------------------------------------------------

  HOME: '/',
  JOBS: '/scripts',
  SOURCING: '/sourcing',
  EXTERNAL_AGENT: '/external-agent',
  INTERVIEWS: '/interviews',
  ANALYTICS: '/analytics',
  QUESTIONS: '/questions',
  NOTES: '/meeting-copilot',
  SUPPORT_FEEDBACK: '/support/feedback',
  SETTINGS: '/settings',
  SETTINGS_TAB: '/settings/:main_tab/:sub_tab',
  USER_SETTINGS: '/user-settings',
  BILLING: '/billing',
  AUDIT_LOG: '/audit-log',
  ALERTS: '/alerts',
  MANAGE_PHONE_NUMBERS: '/support/manage-phone-numbers',

  // ---------------------------------------------------------------------------
  // LLM-navigable: parameterized routes
  // ---------------------------------------------------------------------------

  JOBS_FOLDER: '/scripts/folders/:folder_id',
  JOBS_EDITOR: '/scripts/script-editor/:campaign_id',
  JOBS_FOLDER_EDITOR: '/scripts/folders/:folder_id/script-editor/:campaign_id',
  CAMPAIGN_DETAILS: '/campaigns/campaign/:campaign_id',
  SUBMITTAL_REPORT:
    '/campaigns/:campaign_id/candidates/:candidate_id/calls/:call_id/submittal-report',
  NOTE: '/notes/:note_id',

  ANALYTICS_DASHBOARD: '/analytics/dashboards/:dashboard_id',

  // ---------------------------------------------------------------------------
  // NOT LLM-navigable — auth & OAuth callbacks
  // ---------------------------------------------------------------------------

  LOGIN: '/login',
  AUTHENTICATE: '/authenticate',
  SUPPORT_MICROSOFT_CALLBACK: '/auth/support-microsoft/callback',
  OAUTH_CONSENT: '/oauth/consent',
  OAUTH_REDIRECT: '/oauth-redirect',
  OAUTH_CALENDAR_REDIRECT: '/oauth-calendar-redirect',
  OAUTH_USER_CALENDAR_REDIRECT: '/oauth-user-calendar-redirect',
  OAUTH_NOTIFICATION_REDIRECT: '/oauth-notification-redirect',
  OAUTH_ZOOM_REDIRECT: '/oauth-zoom-redirect',

  // ---------------------------------------------------------------------------
  // NOT LLM-navigable — error / restricted / legacy
  // ---------------------------------------------------------------------------

  RESTRICTED_ACCESS: '/restricted-access',

  // Job-creation flow entry points. Same ScriptTabsPage component as the
  // persisted editor, but the URL prefix (`new` / `new-phone` / `new-web`)
  // is what tells the page it's a brand-new draft and skips the GET-campaign
  // fetch. The `:campaign_id` is a client-generated UUID for an unpersisted
  // record, so these URLs are not deep-linkable — only the in-app "New Job"
  // button produces them, and the chat must NOT direct users here.
  JOBS_EDITOR_NEW: '/scripts/script-editor/new/:campaign_id',
  JOBS_FOLDER_EDITOR_NEW:
    '/scripts/folders/:folder_id/script-editor/new/:campaign_id',
  JOBS_EDITOR_NEW_PHONE: '/scripts/script-editor/new-phone/:campaign_id',
  JOBS_EDITOR_NEW_WEB: '/scripts/script-editor/new-web/:campaign_id',
  JOBS_FOLDER_EDITOR_NEW_PHONE:
    '/scripts/folders/:folder_id/script-editor/new-phone/:campaign_id',
  JOBS_FOLDER_EDITOR_NEW_WEB:
    '/scripts/folders/:folder_id/script-editor/new-web/:campaign_id',
  NOTES_LEGACY: '/notes',
  BILLING_LEGACY: '/support/billing',

  // ---------------------------------------------------------------------------
  // NOT LLM-navigable — iframe entry points (loaded inside add-ons)
  // ---------------------------------------------------------------------------

  NOTES_GOOGLE: '/notes/google',
  NOTES_TEAMS: '/notes/teams',
  NOTES_TEAMS_HOME: '/notes/teams/home',
  NOTES_ZOOM: '/notes/zoom',
  NOTES_ZOOM_SELECT_ORG: '/notes/zoom/select-org',
  BULLHORN_JOB_TAB: '/bullhorn/job-tab',
  BULLHORN_CANDIDATE_TAB: '/bullhorn/candidate-tab',

  // ---------------------------------------------------------------------------
  // NOT LLM-navigable — email-action confirmation pages (auto-fire mutations)
  // ---------------------------------------------------------------------------

  EMAIL_DECISION_ACCEPT: '/email-decision/accept',
  EMAIL_DECISION_DISPOSITION: '/email-decision/disposition',
  APPROVE_REISSUE:
    '/campaign/:campaign_id/candidate/:candidate_id/call/:call_id/approve-reissue',
  REJECT_REISSUE:
    '/campaign/:campaign_id/candidate/:candidate_id/call/:call_id/reject-reissue',

  // ---------------------------------------------------------------------------
  // NOT LLM-navigable — externally shareable / token-gated
  // ---------------------------------------------------------------------------

  SHARE_TRANSCRIPT: '/share-transcript',
  // Opened only from a share link, whose fragment carries the secret; never built in the app.
  SHARED_SUBMITTAL_REPORT: '/shared/submittal-report',
} as const;

export type RouteKey = keyof typeof paths;

/** Any registered path template, e.g. `'/notes/:note_id'`. */
export type AppPathTemplate = (typeof paths)[RouteKey];

/** A registered template with no `:param` segments, e.g. `'/scripts'`. */
export type StaticAppPathTemplate = Exclude<
  AppPathTemplate,
  `${string}:${string}`
>;

export function isRouteKey(key: string): key is RouteKey {
  return Object.hasOwn(paths, key);
}

/** Every key of `paths`, in declaration order. */
export const routeKeys: readonly RouteKey[] =
  Object.keys(paths).filter(isRouteKey);

/**
 * Every template `JobPage` mounts at (`app-shell/routeMounts.ts`). Components
 * rendered only inside it read their params with
 * `useRouteParams(...JOB_PAGE_PATHS)`.
 */
export const JOB_PAGE_PATHS = [
  paths.JOBS_EDITOR,
  paths.JOBS_FOLDER_EDITOR,
  paths.JOBS_EDITOR_NEW,
  paths.JOBS_FOLDER_EDITOR_NEW,
  paths.JOBS_EDITOR_NEW_PHONE,
  paths.JOBS_EDITOR_NEW_WEB,
  paths.JOBS_FOLDER_EDITOR_NEW_PHONE,
  paths.JOBS_FOLDER_EDITOR_NEW_WEB,
] as const;
